import { getApps, initializeApp } from "firebase/app";
import {
    getDatabase,
    onValue,
    push,
    ref,
    serverTimestamp,
    set,
} from "firebase/database";
import { FirebaseConfig } from "../config/FirebaseConfig";

// Initialize Firebase if not already initialized
let app;
if (!getApps().length) {
    app = initializeApp(FirebaseConfig);
} else {
    app = getApps()[0];
}

const database = getDatabase(app);

/**
 * Firebase Realtime Database utility functions for location tracking
 */
class FirebaseRealtime {
    /**
     * Listen to driver location updates in real-time
     * @param {string} bookingId - The booking ID
     * @param {Function} onLocationUpdate - Callback function when location updates
     * @param {Function} onError - Callback function for errors
     * @returns {Function} - Unsubscribe function
     */
    static listenToDriverLocation(bookingId, onLocationUpdate, onError) {
        if (!bookingId) {
            console.error("Booking ID is required for location tracking");
            return null;
        }

        console.log(
            `🔴 Starting real-time location listener for booking: ${bookingId}`
        );

        // Listen to the latest location in tracking/{bookingId}
        const locationRef = ref(database, `tracking/${bookingId}`);

        const unsubscribe = onValue(
            locationRef,
            (snapshot) => {
                if (snapshot.exists()) {
                    const trackingData = snapshot.val();

                    // Get the latest location (last entry)
                    const locationKeys = Object.keys(trackingData);
                    if (locationKeys.length > 0) {
                        // Sort by timestamp to get the latest
                        const sortedKeys = locationKeys.sort((a, b) => {
                            const timeA = trackingData[a].at || 0;
                            const timeB = trackingData[b].at || 0;
                            return timeB - timeA; // Descending order (latest first)
                        });

                        const latestLocation = trackingData[sortedKeys[0]];

                        console.log(
                            "📍 Real-time location update received:",
                            latestLocation
                        );

                        onLocationUpdate({
                            lat: latestLocation.lat,
                            lng: latestLocation.lng,
                            timestamp: latestLocation.at,
                            accuracy: latestLocation.accuracy || null,
                            heading: latestLocation.heading || null,
                            speed: latestLocation.speed || null,
                        });
                    } else {
                        console.log("No location data available yet");
                    }
                } else {
                    console.log(
                        "No tracking data found for booking:",
                        bookingId
                    );
                }
            },
            (error) => {
                console.error("Error listening to driver location:", error);
                if (onError) {
                    onError(error);
                }
            }
        );

        return unsubscribe;
    }

    /**
     * Listen to driver location updates with throttling to prevent too frequent updates
     * @param {string} bookingId - The booking ID
     * @param {Function} onLocationUpdate - Callback function when location updates
     * @param {Function} onError - Callback function for errors
     * @param {number} throttleMs - Throttle interval in milliseconds (default: 5000ms)
     * @returns {Function} - Unsubscribe function
     */
    static listenToDriverLocationThrottled(
        bookingId,
        onLocationUpdate,
        onError,
        throttleMs = 5000
    ) {
        if (!bookingId) {
            console.error("Booking ID is required for location tracking");
            return null;
        }

        console.log(
            `🔴 Starting throttled real-time location listener for booking: ${bookingId} (throttle: ${throttleMs}ms)`
        );

        let lastUpdateTime = 0;
        let lastLocation = null;

        const locationRef = ref(database, `tracking/${bookingId}`);

        const unsubscribe = onValue(
            locationRef,
            (snapshot) => {
                const now = Date.now();

                // Throttle updates
                if (now - lastUpdateTime < throttleMs) {
                    return;
                }

                if (snapshot.exists()) {
                    const trackingData = snapshot.val();
                    const locationKeys = Object.keys(trackingData);

                    if (locationKeys.length > 0) {
                        const sortedKeys = locationKeys.sort((a, b) => {
                            const timeA = trackingData[a].at || 0;
                            const timeB = trackingData[b].at || 0;
                            return timeB - timeA;
                        });

                        const latestLocation = trackingData[sortedKeys[0]];

                        // Check if location actually changed (avoid duplicate updates)
                        if (
                            !lastLocation ||
                            lastLocation.lat !== latestLocation.lat ||
                            lastLocation.lng !== latestLocation.lng
                        ) {
                            console.log(
                                "📍 Throttled real-time location update:",
                                latestLocation
                            );

                            lastLocation = {
                                lat: latestLocation.lat,
                                lng: latestLocation.lng,
                                timestamp: latestLocation.at,
                            };

                            lastUpdateTime = now;

                            onLocationUpdate({
                                lat: latestLocation.lat,
                                lng: latestLocation.lng,
                                timestamp: latestLocation.at,
                                accuracy: latestLocation.accuracy || null,
                                heading: latestLocation.heading || null,
                                speed: latestLocation.speed || null,
                            });
                        }
                    }
                }
            },
            (error) => {
                console.error(
                    "Error listening to driver location (throttled):",
                    error
                );
                if (onError) {
                    onError(error);
                }
            }
        );

        return unsubscribe;
    }

    /**
     * Save driver location to Realtime Database (for mobile app)
     * @param {string} bookingId - The booking ID
     * @param {Object} location - Location data
     * @param {number} location.lat - Latitude
     * @param {number} location.lng - Longitude
     * @param {number} location.accuracy - Location accuracy (optional)
     * @param {number} location.heading - Heading direction (optional)
     * @param {number} location.speed - Speed (optional)
     * @returns {Promise<boolean>} - Success status
     */
    static async saveDriverLocation(bookingId, location) {
        try {
            if (!bookingId || !location || !location.lat || !location.lng) {
                throw new Error(
                    "Booking ID and valid location data are required"
                );
            }

            const locationData = {
                lat: location.lat,
                lng: location.lng,
                at: serverTimestamp(),
                accuracy: location.accuracy || null,
                heading: location.heading || null,
                speed: location.speed || null,
            };

            // Push to tracking/{bookingId} to create a new entry
            const trackingRef = ref(database, `tracking/${bookingId}`);
            const newLocationRef = push(trackingRef);

            await set(newLocationRef, locationData);

            console.log(
                "📍 Driver location saved to Realtime Database:",
                locationData
            );
            return true;
        } catch (error) {
            console.error("Error saving driver location:", error);
            return false;
        }
    }

    /**
     * Get the latest driver location (one-time fetch)
     * @param {string} bookingId - The booking ID
     * @returns {Promise<Object|null>} - Latest location data or null
     */
    static async getLatestDriverLocation(bookingId) {
        try {
            if (!bookingId) {
                throw new Error("Booking ID is required");
            }

            const locationRef = ref(database, `tracking/${bookingId}`);
            const snapshot = await new Promise((resolve, reject) => {
                onValue(locationRef, resolve, reject, { onlyOnce: true });
            });

            if (snapshot.exists()) {
                const trackingData = snapshot.val();
                const locationKeys = Object.keys(trackingData);

                if (locationKeys.length > 0) {
                    const sortedKeys = locationKeys.sort((a, b) => {
                        const timeA = trackingData[a].at || 0;
                        const timeB = trackingData[b].at || 0;
                        return timeB - timeA;
                    });

                    const latestLocation = trackingData[sortedKeys[0]];

                    return {
                        lat: latestLocation.lat,
                        lng: latestLocation.lng,
                        timestamp: latestLocation.at,
                        accuracy: latestLocation.accuracy || null,
                        heading: latestLocation.heading || null,
                        speed: latestLocation.speed || null,
                    };
                }
            }

            return null;
        } catch (error) {
            console.error("Error getting latest driver location:", error);
            return null;
        }
    }

    /**
     * Listen to booking status changes in real-time
     * @param {string} bookingId - The booking ID
     * @param {Function} onStatusUpdate - Callback function when status updates
     * @param {Function} onError - Callback function for errors
     * @returns {Function} - Unsubscribe function
     */
    static listenToBookingStatus(bookingId, onStatusUpdate, onError) {
        if (!bookingId) {
            console.error("Booking ID is required for status tracking");
            return null;
        }

        console.log(
            `🔴 Starting real-time status listener for booking: ${bookingId}`
        );

        // Listen to the booking status in bookings/{bookingId}/status
        const bookingRef = ref(database, `bookings/${bookingId}`);

        const unsubscribe = onValue(
            bookingRef,
            (snapshot) => {
                if (snapshot.exists()) {
                    const bookingData = snapshot.val();

                    console.log(
                        "📈 Real-time status update received:",
                        bookingData.status
                    );

                    onStatusUpdate({
                        status: bookingData.status,
                        timestamp: Date.now(),
                        bookingData: bookingData, // Full booking data for additional info
                        driver_name: bookingData.driver_name,
                        customer_name: bookingData.customer_name,
                        reference: bookingData.reference,
                        pickup: bookingData.pickup,
                        drop: bookingData.drop,
                        trip_cost: bookingData.trip_cost,
                    });
                } else {
                    console.log(
                        "No booking data found for booking:",
                        bookingId
                    );
                }
            },
            (error) => {
                console.error("Error listening to booking status:", error);
                if (onError) {
                    onError(error);
                }
            }
        );

        return unsubscribe;
    }

    /**
     * Listen to booking status changes with throttling to prevent too frequent updates
     * @param {string} bookingId - The booking ID
     * @param {Function} onStatusUpdate - Callback function when status updates
     * @param {Function} onError - Callback function for errors
     * @param {number} throttleMs - Throttle interval in milliseconds (default: 2000ms)
     * @returns {Function} - Unsubscribe function
     */
    static listenToBookingStatusThrottled(
        bookingId,
        onStatusUpdate,
        onError,
        throttleMs = 2000
    ) {
        if (!bookingId) {
            console.error("Booking ID is required for status tracking");
            return null;
        }

        console.log(
            `🔴 Starting throttled real-time status listener for booking: ${bookingId} (throttle: ${throttleMs}ms)`
        );

        let lastUpdateTime = 0;
        let lastStatus = null;

        const bookingRef = ref(database, `bookings/${bookingId}`);

        const unsubscribe = onValue(
            bookingRef,
            (snapshot) => {
                const now = Date.now();

                // Throttle updates
                if (now - lastUpdateTime < throttleMs) {
                    return;
                }

                if (snapshot.exists()) {
                    const bookingData = snapshot.val();

                    // Check if status actually changed (avoid duplicate updates)
                    if (lastStatus !== bookingData.status) {
                        console.log(
                            "📈 Throttled real-time status update:",
                            bookingData.status
                        );

                        lastStatus = bookingData.status;
                        lastUpdateTime = now;

                        onStatusUpdate({
                            status: bookingData.status,
                            timestamp: now,
                            bookingData: bookingData,
                            driver_name: bookingData.driver_name,
                            customer_name: bookingData.customer_name,
                            reference: bookingData.reference,
                            pickup: bookingData.pickup,
                            drop: bookingData.drop,
                            trip_cost: bookingData.trip_cost,
                        });
                    }
                }
            },
            (error) => {
                console.error(
                    "Error listening to booking status (throttled):",
                    error
                );
                if (onError) {
                    onError(error);
                }
            }
        );

        return unsubscribe;
    }

    /**
     * Update booking status in Realtime Database
     * @param {string} bookingId - The booking ID
     * @param {string} newStatus - New status value
     * @param {Object} additionalData - Additional data to update (optional)
     * @returns {Promise<boolean>} - Success status
     */
    static async updateBookingStatus(
        bookingId,
        newStatus,
        additionalData = {}
    ) {
        try {
            if (!bookingId || !newStatus) {
                throw new Error("Booking ID and status are required");
            }

            const updateData = {
                status: newStatus,
                ...additionalData,
                status_updated_at: serverTimestamp(),
            };

            const bookingRef = ref(database, `bookings/${bookingId}`);
            await set(bookingRef, updateData);

            console.log(
                "📈 Booking status updated in Realtime Database:",
                updateData
            );
            return true;
        } catch (error) {
            console.error("Error updating booking status:", error);
            return false;
        }
    }

    /**
     * Get current booking status (one-time fetch)
     * @param {string} bookingId - The booking ID
     * @returns {Promise<Object|null>} - Current booking data or null
     */
    static async getCurrentBookingStatus(bookingId) {
        try {
            if (!bookingId) {
                throw new Error("Booking ID is required");
            }

            const bookingRef = ref(database, `bookings/${bookingId}`);
            const snapshot = await new Promise((resolve, reject) => {
                onValue(bookingRef, resolve, reject, { onlyOnce: true });
            });

            if (snapshot.exists()) {
                const bookingData = snapshot.val();
                return {
                    status: bookingData.status,
                    driver_name: bookingData.driver_name,
                    customer_name: bookingData.customer_name,
                    reference: bookingData.reference,
                    pickup: bookingData.pickup,
                    drop: bookingData.drop,
                    trip_cost: bookingData.trip_cost,
                    bookingData: bookingData,
                };
            }

            return null;
        } catch (error) {
            console.error("Error getting current booking status:", error);
            return null;
        }
    }

    /**
     * Clean up old location data (keep only last N entries)
     * @param {string} bookingId - The booking ID
     * @param {number} keepCount - Number of recent locations to keep (default: 50)
     * @returns {Promise<boolean>} - Success status
     */
    static async cleanupOldLocations(bookingId, keepCount = 50) {
        try {
            if (!bookingId) {
                throw new Error("Booking ID is required");
            }

            const locationRef = ref(database, `tracking/${bookingId}`);
            const snapshot = await new Promise((resolve, reject) => {
                onValue(locationRef, resolve, reject, { onlyOnce: true });
            });

            if (snapshot.exists()) {
                const trackingData = snapshot.val();
                const locationKeys = Object.keys(trackingData);

                if (locationKeys.length > keepCount) {
                    // Sort by timestamp (oldest first)
                    const sortedKeys = locationKeys.sort((a, b) => {
                        const timeA = trackingData[a].at || 0;
                        const timeB = trackingData[b].at || 0;
                        return timeA - timeB; // Ascending order (oldest first)
                    });

                    // Remove old entries
                    const keysToRemove = sortedKeys.slice(
                        0,
                        locationKeys.length - keepCount
                    );
                    const updates = {};

                    keysToRemove.forEach((key) => {
                        updates[`tracking/${bookingId}/${key}`] = null;
                    });

                    // Batch update to remove old entries
                    const rootRef = ref(database);
                    await set(rootRef, updates);

                    console.log(
                        `🧹 Cleaned up ${keysToRemove.length} old location entries for booking: ${bookingId}`
                    );
                }
            }

            return true;
        } catch (error) {
            console.error("Error cleaning up old locations:", error);
            return false;
        }
    }
}

export default FirebaseRealtime;
